import { maxCapFromWarmup, rampDaysFromWarmup } from '../constants';
import type { GeneratedPlan, PlanConfig, Segment, WarmupStep } from '../types';

/** Raw (non-segment) config fields. */
export type RawPlanConfig = Omit<PlanConfig, 'segs'>;

const DEFAULT_LAUNCH_SETTINGS = {
  unsold_lead_pickup_threshold_days: 30,
  new_lead_entry_threshold_days: 90,
  look_back_start_date: null as string | null,
  look_back_end_date: null as string | null,
  retargeting_eligibility_cutoff_years: 2,
};

/** Placeholder before API load — not used for initialize defaults (server-owned). */
export const EMPTY_RAW_CONFIG: RawPlanConfig = {
  total: 0,
  monthly: 0,
  look: 0,
  cap: 0,
  ramp: 0,
  freqI: 0,
  freqS: 0,
  rampCad: 1,
  ssCad: 1,
  newStart: 1,
  warmupSchedule: [],
  eagerProvisioning: false,
  unsold_lead_pickup_threshold_days:
    DEFAULT_LAUNCH_SETTINGS.unsold_lead_pickup_threshold_days,
  new_lead_entry_threshold_days: DEFAULT_LAUNCH_SETTINGS.new_lead_entry_threshold_days,
  look_back_start_date: DEFAULT_LAUNCH_SETTINGS.look_back_start_date,
  look_back_end_date: DEFAULT_LAUNCH_SETTINGS.look_back_end_date,
  retargeting_eligibility_cutoff_years:
    DEFAULT_LAUNCH_SETTINGS.retargeting_eligibility_cutoff_years,
};

export interface NurturingPlanRequestBody {
  totalLeads: number;
  monthlyNewLeads: number;
  lookbackMonths: number;
  capPerAddress: number;
  rampDays: number;
  freqInitDays: number;
  freqSteadyDays: number;
  rampUpBatchCadenceDays: number;
  steadyStateBatchCadenceDays: number;
  newLeadsStartDay: number;
  warmupSchedule: WarmupStep[];
  eagerProvisioning: boolean;
  segments: Array<{
    name: string;
    pct: number;
    startDay: number;
    order: number;
    count: number;
  }>;
}

export interface NurturingPlanSettingsBody extends NurturingPlanRequestBody {
  // Launch settings are part of the nurturing plan generator dealer-settings record.
  unsold_lead_pickup_threshold_days: number;
  new_lead_entry_threshold_days: number;
  look_back_start_date: string | null;
  look_back_end_date: string | null;
  /** Legacy alias kept for backward-compatible API payloads. */
  look_back_date: string | null;
  retargeting_eligibility_cutoff_years: number;

  generatedPlan: GeneratedPlan;
}

function parseWarmupSchedule(raw: unknown): WarmupStep[] {
  if (!Array.isArray(raw) || raw.length === 0) {
    return [];
  }
  return raw.map((item) => {
    const s = (item ?? {}) as Record<string, unknown>;
    return {
      dayFrom: Math.max(1, num(s.dayFrom, 1)),
      dayTo: Math.max(1, num(s.dayTo, 1)),
      emails: Math.max(1, num(s.emails, 1)),
    };
  });
}

/** Serialise the editable plan config into the backend body shape. */
export function configToRequestBody(config: PlanConfig): NurturingPlanRequestBody {
  const warmup = config.warmupSchedule;
  return {
    totalLeads: config.total,
    monthlyNewLeads: config.monthly,
    lookbackMonths: config.look,
    capPerAddress: maxCapFromWarmup(warmup),
    rampDays: rampDaysFromWarmup(warmup),
    freqInitDays: config.freqI,
    freqSteadyDays: config.freqS,
    rampUpBatchCadenceDays: config.rampCad,
    steadyStateBatchCadenceDays: config.ssCad,
    newLeadsStartDay: config.newStart,
    warmupSchedule: warmup.map((s) => ({ ...s })),
    eagerProvisioning: config.eagerProvisioning,
    segments: config.segs.map((s) => ({
      name: s.name,
      pct: s.pct,
      startDay: s.startDay,
      order: s.order,
      count: s.count,
    })),
  };
}

export function buildSavePayload(
  config: PlanConfig,
  generatedPlan: GeneratedPlan,
): NurturingPlanSettingsBody {
  return {
    ...configToRequestBody(config),
    unsold_lead_pickup_threshold_days: config.unsold_lead_pickup_threshold_days,
    new_lead_entry_threshold_days: config.new_lead_entry_threshold_days,
    look_back_start_date: config.look_back_start_date || null,
    look_back_end_date: config.look_back_end_date || null,
    look_back_date: config.look_back_end_date || null,
    retargeting_eligibility_cutoff_years:
      config.retargeting_eligibility_cutoff_years,
    generatedPlan,
  };
}

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);

const looksLikePlanProperties = (value: Record<string, unknown>): boolean =>
  'totalLeads' in value || 'segments' in value || 'monthlyNewLeads' in value;

export function extractSettingsBody(response: unknown): Record<string, unknown> | null {
  if (!isPlainObject(response)) return null;
  const r = response as Record<string, any>;

  // GET dealer-settings grouped envelope: body[0].settings[0].properties
  if (Array.isArray(r.body) && r.body[0]?.settings?.length) {
    const props = r.body[0].settings[0]?.properties;
    if (isPlainObject(props)) return props;
  }

  // Initialize / get-by-id: body is a dealer_settings document with nested properties
  if (isPlainObject(r.body?.properties)) {
    return r.body.properties as Record<string, unknown>;
  }

  if (isPlainObject(r.properties)) return r.properties;

  if (isPlainObject(r.body) && looksLikePlanProperties(r.body as Record<string, unknown>)) {
    return r.body as Record<string, unknown>;
  }

  if (isPlainObject(r.data?.properties)) {
    return r.data.properties as Record<string, unknown>;
  }
  if (isPlainObject(r.data) && looksLikePlanProperties(r.data as Record<string, unknown>)) {
    return r.data as Record<string, unknown>;
  }
  if (isPlainObject(r.config)) return r.config;
  if (looksLikePlanProperties(r)) return r;
  return null;
}

const num = (value: unknown, fallback: number): number => {
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return fallback;
    const asNum = Number(trimmed);
    return Number.isNaN(asNum) ? fallback : asNum;
  }
  return fallback;
};

const parseOptionalDate = (value: unknown, fallback: string | null = null): string | null => {
  if (typeof value === 'string' && value.trim()) {
    return value;
  }
  return fallback;
};

export function requestBodyToConfig(
  body: Record<string, unknown> | null,
): { rawConfig: RawPlanConfig; segments: Segment[] } | null {
  if (!body) return null;
  const hasFields =
    'totalLeads' in body || 'segments' in body || 'monthlyNewLeads' in body;
  if (!hasFields) return null;

  const warmupSchedule = parseWarmupSchedule(body.warmupSchedule);

  const rawConfig: RawPlanConfig = {
    total: num(body.totalLeads, 0),
    monthly: num(body.monthlyNewLeads, 0),
    look: num(body.lookbackMonths, 0),
    cap: num(body.capPerAddress, maxCapFromWarmup(warmupSchedule)),
    ramp: num(body.rampDays, rampDaysFromWarmup(warmupSchedule)),
    freqI: num(body.freqInitDays, 0),
    freqS: num(body.freqSteadyDays, 0),
    rampCad: 1,
    ssCad: 1,
    newStart: Math.max(1, num(body.newLeadsStartDay, 1)),
    warmupSchedule,
    eagerProvisioning: Boolean(body.eagerProvisioning),
    unsold_lead_pickup_threshold_days: num(
      body.unsold_lead_pickup_threshold_days,
      DEFAULT_LAUNCH_SETTINGS.unsold_lead_pickup_threshold_days,
    ),
    new_lead_entry_threshold_days: num(
      body.new_lead_entry_threshold_days,
      DEFAULT_LAUNCH_SETTINGS.new_lead_entry_threshold_days,
    ),
    look_back_start_date: parseOptionalDate(body.look_back_start_date),
    look_back_end_date: parseOptionalDate(
      body.look_back_end_date,
      parseOptionalDate(body.look_back_date),
    ),
    retargeting_eligibility_cutoff_years: num(
      body.retargeting_eligibility_cutoff_years,
      DEFAULT_LAUNCH_SETTINGS.retargeting_eligibility_cutoff_years,
    ),
  };

  const rawSegments = Array.isArray(body.segments) ? body.segments : [];
  const segments: Segment[] = rawSegments
    .map((raw, i) => {
      const s = (raw ?? {}) as Record<string, unknown>;
      return {
        id: i + 1,
        name: typeof s.name === 'string' ? s.name : `Segment ${i + 1}`,
        pct: num(s.pct, 0),
        startDay: Math.max(1, num(s.startDay, 1)),
        order: num(s.order, i + 1),
      };
    })
    .sort((a, b) => a.order - b.order)
    .map((s, i) => ({ ...s, id: i + 1, order: i + 1 }));

  return { rawConfig, segments };
}

const isGeneratedPlan = (value: unknown): value is GeneratedPlan => {
  if (!isPlainObject(value)) return false;
  const p = value;
  return (
    isPlainObject(p.summary) &&
    Array.isArray(p.phases) &&
    Array.isArray(p.batches) &&
    Array.isArray(p.addrEvents) &&
    Array.isArray(p.starts) &&
    typeof p.addrsSteady === 'number' &&
    typeof p.mid === 'number'
  );
};

export function extractGeneratedPlan(
  body: Record<string, unknown> | null,
): GeneratedPlan | null {
  if (!body || !isGeneratedPlan(body.generatedPlan)) return null;
  return body.generatedPlan;
}
